import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Phase 3 · Portfolio Merge Preview.
 * Shows every building whose property_configs have same-unit duplicates
 * post-Phase-2 backfill, lets admin approve merges one at a time.
 *
 * Group card layout:
 *   [Address · Landlord]      [X rows · Y dup rows · Z units]
 *     Worst-offending unit inline: 3 rows for "Unit 1"
 *       ⭐ Keep this row (richest)   [ Merge N into this row ]
 *       row 2  (fewer fields)         Skip
 *       row 3  (empty)
 *     "See full breakdown" → modal with every unit
 */
export default function AdminPortfolioMergePreview() {
  const [groups, setGroups] = useState(null);
  const [busy, setBusy] = useState(null);
  const [modalGroup, setModalGroup] = useState(null); // full breakdown
  const [modalDetail, setModalDetail] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/portfolio-merge/groups");
      setGroups(r.data.groups);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    }
  };
  useEffect(() => { load(); }, []);

  const doMerge = async (group) => {
    const keeperId = group.suggested_keep_id;
    const archiveIds = group.worst_unit_rows.filter(r => r.id !== keeperId).map(r => r.id);
    if (!window.confirm(`Merge ${archiveIds.length} duplicate row${archiveIds.length === 1 ? "" : "s"} for "${group.display_address} · Unit ${group.worst_unit}"?\n\nThe richest row (with the most fields populated) is kept. Others archive to property_configs_archive_dedupe — reversible.`)) return;
    setBusy(group.canonical_key);
    try {
      const r = await api.post("/admin/portfolio-merge/merge", {
        canonical_key: group.canonical_key,
        keep_id: keeperId,
        archive_ids: archiveIds,
      });
      toast.success(`Archived ${r.data.archived} · kept 1`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Merge failed");
    } finally { setBusy(null); }
  };

  const doSkip = async (group) => {
    if (!window.confirm(`Skip "${group.display_address}"?\n\nIt won't show up here again unless you unskip.`)) return;
    setBusy(group.canonical_key);
    try {
      await api.post("/admin/portfolio-merge/skip", { canonical_key: group.canonical_key });
      toast.success(`Skipped ${group.display_address}`);
      setGroups(prev => prev.filter(g => g.canonical_key !== group.canonical_key));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Skip failed");
    } finally { setBusy(null); }
  };

  const openBreakdown = async (group) => {
    setModalGroup(group);
    setModalDetail(null);
    try {
      const r = await api.get(`/admin/portfolio-merge/group?canonical_key=${encodeURIComponent(group.canonical_key)}`);
      setModalDetail(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    }
  };

  const totalDups = (groups || []).reduce((s, g) => s + g.dup_rows_within, 0);

  return (
    <AdminShell>
      <div className="max-w-5xl mx-auto p-5" data-testid="portfolio-merge-preview">
        <div className="flex items-center gap-3 mb-1 flex-wrap">
          <h1 className="text-2xl font-bold text-[#0B1F3A] tracking-tight">Duplicate Buildings</h1>
          <span className="text-[11px] font-bold uppercase tracking-widest text-red-700 bg-red-100 px-2.5 py-1 rounded-full">
            Phase 3 · needs review
          </span>
          <button onClick={load} data-testid="pm-reload"
            className="ml-auto text-xs font-bold text-[#008a8f] hover:text-[#0B1F3A] underline">
            Reload
          </button>
        </div>
        <p className="text-sm text-slate-500 mb-6 max-w-2xl">
          Every building below has two-plus property records pointing at the same unit — usually because the address was typed slightly differently on separate imports. Preview each group, then merge or skip. Merged rows archive to <code className="bg-slate-100 px-1.5 py-0.5 rounded text-[11px]">property_configs_archive_dedupe</code> and are reversible.
        </p>

        {groups === null && (
          <div className="text-sm text-slate-400 p-8 text-center" data-testid="pm-loading">Loading…</div>
        )}
        {groups !== null && groups.length === 0 && (
          <div className="bg-white rounded-2xl p-10 text-center shadow-sm" data-testid="pm-empty">
            <div className="text-5xl mb-3">🎉</div>
            <div className="text-lg font-bold text-[#0B1F3A]">No duplicate buildings.</div>
            <div className="text-sm text-slate-500 mt-1">Your portfolio is clean.</div>
          </div>
        )}
        {groups !== null && groups.length > 0 && (
          <>
            <div className="mb-4 text-xs text-slate-600" data-testid="pm-summary">
              <b>{groups.length}</b> buildings with duplicates · <b>{totalDups}</b> rows would archive if you merged everything
            </div>
            <div className="flex flex-col gap-3">
              {groups.map(g => (
                <GroupCard key={g.canonical_key} group={g}
                  busy={busy === g.canonical_key}
                  onMerge={() => doMerge(g)}
                  onSkip={() => doSkip(g)}
                  onOpen={() => openBreakdown(g)}/>
              ))}
            </div>
          </>
        )}

        {modalGroup && (
          <BreakdownModal group={modalGroup} detail={modalDetail}
            onClose={() => { setModalGroup(null); setModalDetail(null); }}/>
        )}

        <div className="mt-8 text-[11px] text-slate-400">
          <Link to="/admin" className="underline">← Admin home</Link>
        </div>
      </div>
    </AdminShell>
  );
}

function GroupCard({ group, busy, onMerge, onSkip, onOpen }) {
  const archiveCount = group.worst_unit_rows.length - 1;
  return (
    <div className="bg-white rounded-2xl shadow-sm p-4" data-testid={`pm-group-${group.canonical_key.replace(/\s+/g, "-")}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-base font-bold text-[#0B1F3A] truncate">{group.display_address || "(blank address)"}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {group.landlord_name && <>Landlord: <b className="text-slate-700">{group.landlord_name}</b> · </>}
            {group.total_rows} row{group.total_rows === 1 ? "" : "s"} · {group.distinct_unit_count} distinct unit{group.distinct_unit_count === 1 ? "" : "s"} · {group.dup_unit_count} dup unit{group.dup_unit_count === 1 ? "" : "s"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onOpen} data-testid={`pm-preview-${group.canonical_key.replace(/\s+/g, "-")}`}
            className="h-8 px-3 rounded-lg bg-white border border-slate-200 text-[#0B1F3A] text-xs font-bold hover:bg-slate-50">
            Preview
          </button>
          <button onClick={onSkip} disabled={busy} data-testid={`pm-skip-${group.canonical_key.replace(/\s+/g, "-")}`}
            className="h-8 px-3 rounded-lg bg-white border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50 disabled:opacity-50">
            Skip
          </button>
          <button onClick={onMerge} disabled={busy} data-testid={`pm-merge-${group.canonical_key.replace(/\s+/g, "-")}`}
            className="h-8 px-3 rounded-lg bg-[#00C4CC] hover:bg-[#00a8af] text-white text-xs font-bold disabled:opacity-50">
            {busy ? "Merging…" : `Merge ${archiveCount} → 1`}
          </button>
        </div>
      </div>

      <div className="mt-3 border-l-4 border-red-400 bg-red-50 rounded-r-lg p-3">
        <div className="text-[11px] font-bold uppercase tracking-widest text-red-800 mb-1.5">
          Worst offender · Unit {group.worst_unit} · {group.worst_unit_rows.length} rows
        </div>
        <div className="flex flex-col gap-1.5">
          {group.worst_unit_rows.map((r, i) => (
            <div key={r.id} className={`flex items-center gap-2 text-xs p-2 rounded ${i === 0 ? "bg-emerald-50 border border-emerald-200" : "bg-white border border-slate-200"}`}
              data-testid={`pm-row-${r.id}`}>
              <span className={`w-16 text-center font-bold rounded-full py-0.5 flex-shrink-0
                ${i === 0 ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-600"}`}>
                {i === 0 ? "⭐ KEEP" : "ARCHIVE"}
              </span>
              <span className="font-mono text-[10px] text-slate-400 flex-shrink-0">{r.id.slice(-6)}</span>
              <span className="truncate flex-1 text-slate-700">
                {r.address}{r.unit ? ` · Unit ${r.unit}` : ""}
                {r.portfolio_name && r.portfolio_name !== r.address && <span className="text-slate-400"> · {r.portfolio_name}</span>}
              </span>
              <span className="text-[10px] text-slate-500 flex-shrink-0">
                rich <b className="text-slate-700">{r.richness}</b>
                {r.notes_len > 0 && <> · notes <b className="text-slate-700">{r.notes_len}c</b></>}
                {r.landlord_id && <> · has landlord</>}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BreakdownModal({ group, detail, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="pm-modal-backdrop">
      <div className="bg-white rounded-2xl p-5 w-full max-w-3xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="pm-modal">
        <div className="flex items-center gap-3 mb-3">
          <div>
            <div className="text-lg font-bold text-[#0B1F3A]">{group.display_address}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Canonical key: <code className="bg-slate-100 px-1 rounded">{group.canonical_key}</code>
              {" · "}{group.total_rows} rows total
            </div>
          </div>
          <button onClick={onClose} data-testid="pm-modal-close"
            className="ml-auto w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-[#0B1F3A]">×</button>
        </div>
        {!detail && <div className="text-sm text-slate-400 p-6 text-center">Loading full breakdown…</div>}
        {detail && (
          <div className="flex flex-col gap-3">
            {Object.entries(detail.unit_groups).map(([unit, rows]) => (
              <div key={unit} className="border border-slate-200 rounded-xl p-3">
                <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 flex items-center justify-between">
                  <span>Unit {unit}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${rows.length > 1 ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {rows.length} row{rows.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  {rows.map((r, i) => (
                    <div key={r.id} className={`flex items-center gap-2 text-xs p-2 rounded
                      ${rows.length > 1 && i === 0 ? "bg-emerald-50 border border-emerald-200"
                        : rows.length > 1 ? "bg-white border border-slate-200"
                        : "bg-slate-50"}`}>
                      <span className="font-mono text-[10px] text-slate-400 flex-shrink-0">{r.id.slice(-6)}</span>
                      <span className="truncate flex-1 text-slate-700">
                        <b className="text-[#0B1F3A]">{r.address}</b>
                        {r.portfolio_name && r.portfolio_name !== r.address && <span className="text-slate-400"> · {r.portfolio_name}</span>}
                      </span>
                      <span className="text-[10px] text-slate-500 flex-shrink-0">
                        rich {r.richness}
                        {r.notes_len > 0 && <> · notes {r.notes_len}c</>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
